import { COUNTRY_ALIASES } from "./aliases";
import { expandNameVariants } from "../lib/name-variants";

export interface CountryFact {
  capital: string | null;
  flagDescription: string;
  fact: string;
}

export interface CountryMetadata {
  iso: string;
  // ISO 3166-1 alpha-2 in lowercase, matching the flag SVG filename.
  // Null for unrecognized entities like Somaliland / Northern Cyprus.
  iso2: string | null;
  canonicalName: string;
  names: string[];
  region: string;
  subregion: string | null;
  continent: string | null;
  population: number | null;
  label: { lat: number | null; lng: number | null };
  fact: CountryFact | null;
}

export type CountriesTable = Record<string, CountryMetadata>;

const COUNTRIES_URL = `${import.meta.env.BASE_URL}data/countries.json`;

let cached: CountriesTable | null = null;
let inflight: Promise<CountriesTable> | null = null;

export const loadCountries = async (): Promise<CountriesTable> => {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = fetch(COUNTRIES_URL)
    .then((res) => {
      if (!res.ok) throw new Error(`countries fetch failed: ${res.status}`);
      return res.json() as Promise<CountriesTable>;
    })
    .then((data) => {
      cached = data;
      inflight = null;
      return data;
    });
  return inflight;
};

// All acceptable spellings for a given country: canonical name, GeoJSON
// names, curated aliases, and programmatically-derived short forms (strip
// "Republic of"-style prefixes, accept the part before " and " for
// compound country names like "Bosnia and Herzegovina").
export const acceptedNamesFor = (country: CountryMetadata): string[] => {
  const set = new Set<string>();
  const addWithVariants = (name: string): void => {
    for (const variant of expandNameVariants(name)) set.add(variant);
  };
  addWithVariants(country.canonicalName);
  for (const name of country.names) addWithVariants(name);
  const extras = COUNTRY_ALIASES[country.iso];
  if (extras) for (const alias of extras) addWithVariants(alias);
  return [...set];
};

// Path to the local flag SVG for a country. Null when we don't have an
// alpha-2 code (unrecognized entities). Callers should hide the img when
// null rather than rendering a broken link.
export const flagUrlFor = (country: CountryMetadata): string | null =>
  country.iso2 ? `${import.meta.env.BASE_URL}flags/${country.iso2}.svg` : null;

export const groupByRegion = (
  table: CountriesTable,
): Record<string, CountryMetadata[]> => {
  const out: Record<string, CountryMetadata[]> = {};
  for (const country of Object.values(table)) {
    (out[country.region] ??= []).push(country);
  }
  for (const region of Object.keys(out)) {
    out[region]!.sort((a, b) => a.canonicalName.localeCompare(b.canonicalName));
  }
  return out;
};

// Stable ordering for the picker UI — matches the spec's "8-region split".
export const REGION_ORDER: readonly string[] = [
  "Europe",
  "North Africa & Middle East",
  "Sub-Saharan Africa",
  "Central Asia",
  "East Asia",
  "South & Southeast Asia",
  "Oceania",
  "Americas (North/Central/Caribbean)",
  "Americas (South)",
] as const;
